import { addDays, endOfMonth, startOfDay, startOfMonth } from "date-fns";

/** Maksymalny horyzont rezerwacji w sprzedaży kalendarzowej (dni od dziś). */
export const CALENDAR_HORIZON_DAYS = 240;

// Klucz dnia w czasie lokalnym. Celowo bez date-fns format() – ta funkcja leci setki razy przy budowie zbiorów
// i dla każdej komórki kalendarza, a format() parsuje wzorzec przy każdym wywołaniu.
const dayKey = (date: Date) => date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();

export interface CalendarBounds {
  from: Date;
  /** Brak górnej granicy – dzień da się wybrać dowolnie daleko w przód. */
  to?: Date;
  /** Dni niedostępne, ale nadal klikalne (grafik, korty). */
  unavailableDates?: Date[];
}

export interface CalendarAvailability {
  isDisabled: (date: Date) => boolean;
  isSoldOut: (date: Date) => boolean;
  isUnavailable: (date: Date) => boolean;
  /** Dzień do wyboru i z wolnymi terminami (nie wyłączony, nie wyprzedany, nie oznaczony jako niedostępny). */
  isAvailable: (date: Date) => boolean;
  monthHasAvailableDay: (month: Date) => boolean;
  /** Najbliższy dostępny dzień od `from` (włącznie) albo undefined. */
  findAvailableFrom: (from: Date) => Date | undefined;
}

export function createCalendarAvailability(
  disabledDates: Date[],
  soldOutDates: Date[],
  bounds: CalendarBounds,
  horizonDays: number = CALENDAR_HORIZON_DAYS
): CalendarAvailability {
  const disabled = new Set(disabledDates.map(dayKey));
  const soldOut = new Set(soldOutDates.map(dayKey));
  const unavailable = new Set((bounds.unavailableDates ?? []).map(dayKey));
  const first = startOfDay(bounds.from);
  const last = bounds.to ? startOfDay(bounds.to) : undefined;
  const firstKey = dayKey(first);
  const lastKey = last ? dayKey(last) : undefined;
  const searchLast = last ?? addDays(first, horizonDays - 1);

  const inWindow = (date: Date) => {
    const key = dayKey(date);
    if (key < firstKey) return false;
    if (lastKey !== undefined && key > lastKey) return false;
    return true;
  };
  const isDisabled = (date: Date) => !inWindow(date) || disabled.has(dayKey(date));
  const isSoldOut = (date: Date) => soldOut.has(dayKey(date));
  const isUnavailable = (date: Date) => unavailable.has(dayKey(date));
  const isAvailable = (date: Date) => !isDisabled(date) && !isSoldOut(date) && !isUnavailable(date);

  const findAvailableFrom = (from: Date) => {
    let cursor = startOfDay(from) < first ? first : startOfDay(from);
    while (cursor <= searchLast) {
      if (isAvailable(cursor)) return cursor;
      cursor = addDays(cursor, 1);
    }
    return undefined;
  };

  const monthHasAvailableDay = (month: Date) => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const candidate = findAvailableFrom(monthStart);
    return candidate !== undefined && candidate <= monthEnd;
  };

  return { isDisabled, isSoldOut, isUnavailable, isAvailable, monthHasAvailableDay, findAvailableFrom };
}
